import { Fragment, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  Badge,
  Button,
  Drawer,
  styled,
  IconButton,
  ThemeProvider,
  Typography,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  Skeleton,
} from "@mui/material";
import {
  Notifications,
  NotificationsActive,
  NotificationsNoneOutlined,
  Close,
  Refresh,
  PushPin,
  PushPinOutlined,
  DoneAll,
  MarkEmailReadOutlined,
  DraftsOutlined,
  DeleteOutline,
  InfoOutlined,
  WarningAmberOutlined,
  CheckCircleOutline,
  CampaignOutlined,
  OpenInNewOutlined,
  AccessTimeRounded,
  ScheduleRounded,
} from "@mui/icons-material";
import moment from "moment";

import useSettings from "app/hooks/useSettings";
import useNotification from "app/hooks/useNotification";
import useAuth from "app/hooks/useAuth";
import { topBarHeight } from "app/utils/constant";

// ─── SOLID HIGH-CONTRAST CATEGORY METADATA ───────────────────────────────────

const CATEGORY_MAP = {
  alert: {
    label: "Alert",
    Icon: WarningAmberOutlined,
    color: "#b45309",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  warning: {
    label: "Alert",
    Icon: WarningAmberOutlined,
    color: "#b45309",
    bg: "#fffbeb",
    border: "#fde68a",
  },
  announcement: {
    label: "Announcement",
    Icon: CampaignOutlined,
    color: "#6d28d9",
    bg: "#f5f3ff",
    border: "#ddd6fe",
  },
  notice: {
    label: "Notice",
    Icon: CheckCircleOutline,
    color: "#047857",
    bg: "#ecfdf5",
    border: "#a7f3d0",
  },
  success: {
    label: "Notice",
    Icon: CheckCircleOutline,
    color: "#047857",
    bg: "#ecfdf5",
    border: "#a7f3d0",
  },
  system: {
    label: "System",
    Icon: AccessTimeRounded,
    color: "#334155",
    bg: "#f1f5f9",
    border: "#cbd5e1",
  },
  info: {
    label: "Info",
    Icon: InfoOutlined,
    color: "#1d4ed8",
    bg: "#eff6ff",
    border: "#bfdbfe",
  },
};

function getCategoryMeta(type) {
  const key = (type || "info").toLowerCase();
  return CATEGORY_MAP[key] || CATEGORY_MAP.info;
}

// ─── STYLED COMPONENTS ───────────────────────────────────────────────────────

const HeaderBox = styled("div")(({ theme }) => ({
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: topBarHeight + 8,
  borderBottom: "1px solid #e2e8f0",
  background: "#ffffff",
  position: "sticky",
  top: 0,
  zIndex: 10,
}));

const NotificationCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "isUnread" && prop !== "isPinned",
})(({ isUnread, isPinned }) => ({
  position: "relative",
  borderRadius: 12,
  padding: "14px 16px",
  marginBottom: 12,
  backgroundColor: isPinned
    ? "#fffdf5"
    : isUnread
      ? "#ffffff"
      : "#f8fafc",
  border: isPinned
    ? "1.5px solid #f59e0b"
    : isUnread
      ? "1.5px solid #93c5fd"
      : "1px solid #e2e8f0",
  borderLeft: isPinned
    ? "5px solid #d97706"
    : isUnread
      ? "5px solid #2563eb"
      : "5px solid #94a3b8",
  boxShadow: isPinned
    ? "0 4px 12px rgba(245, 158, 11, 0.12)"
    : isUnread
      ? "0 4px 12px rgba(37, 99, 235, 0.08)"
      : "0 1px 3px rgba(0, 0, 0, 0.04)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
  },
}));

export default function NotificationBar({ container }) {
  const theme = useTheme();
  const { settings } = useSettings();
  const { user } = useAuth();

  const [panelOpen, setPanelOpen] = useState(false);
  const [filterType, setFilterType] = useState("all"); // 'all' | 'unread' | 'pinned'

  // Context: Real notification messages loaded from backend API
  const {
    notifications,
    unreadCount,
    pinnedCount,
    loading,
    getNotifications,
    markAsRead,
    markAsUnread,
    togglePin,
    markAllAsRead,
    deleteNotification,
    clearNotifications,
  } = useNotification();

  const handleDrawerToggle = () => setPanelOpen((prev) => !prev);

  // Filtered notifications list
  const displayedNotifications = useMemo(() => {
    let list = [...notifications];

    if (filterType === "unread") {
      list = list.filter((n) => !n.IsRead);
    } else if (filterType === "pinned") {
      list = list.filter((n) => Boolean(n.IsPinned));
    }

    // Always keep pinned on top, then newest first
    return list.sort((a, b) => {
      if (Boolean(b.IsPinned) !== Boolean(a.IsPinned)) {
        return (b.IsPinned ? 1 : 0) - (a.IsPinned ? 1 : 0);
      }
      return new Date(b.CreatedOn || 0) - new Date(a.CreatedOn || 0);
    });
  }, [notifications, filterType]);

  return (
    <Fragment>
      {/* Top Bar Bell Button with Unread Badge */}
      <Tooltip
        title={
          unreadCount > 0
            ? `${unreadCount} Unread Notification${unreadCount > 1 ? "s" : ""}`
            : "Notifications"
        }
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            transition: "transform 0.2s ease",
            "&:hover": { transform: "scale(1.08)" },
          }}
        >
          <Badge
            badgeContent={unreadCount > 0 ? unreadCount : undefined}
            color="error"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontWeight: 800,
                fontSize: "0.7rem",
                height: 18,
                minWidth: 18,
                padding: "0 4px",
                boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
              },
            }}
          >
            {unreadCount > 0 ? (
              <NotificationsActive sx={{ color: "error.main" }} />
            ) : (
              <Notifications sx={{ color: "text.primary" }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Slide-out Drawer */}
      <ThemeProvider theme={settings.themes[settings.activeTheme]}>
        <Drawer
          container={container}
          variant="temporary"
          anchor="right"
          open={panelOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: { xs: "100vw", sm: 420 },
              maxWidth: "100vw",
              bgcolor: "#f8fafc",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
            },
          }}
        >
          {/* ── Header ── */}
          <HeaderBox>
            <Box display="flex" alignItems="center" gap={1.25}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  bgcolor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Notifications sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                    Notifications
                  </Typography>
                  {unreadCount > 0 && (
                    <Chip
                      label={`${unreadCount} new`}
                      size="small"
                      sx={{
                        height: 19,
                        fontSize: "0.65rem",
                        fontWeight: 800,
                        bgcolor: "#dc2626",
                        color: "#ffffff",
                        "& .MuiChip-label": { px: 0.7 },
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.75rem" }}>
                  System alerts, notices & announcements
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Refresh Notifications from Server">
                <IconButton
                  size="small"
                  onClick={getNotifications}
                  disabled={loading}
                  sx={{
                    p: 0.75,
                    color: "#475569",
                    bgcolor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: 1.5,
                    "&:hover": { bgcolor: "#e2e8f0", color: "#2563eb" },
                  }}
                >
                  <Refresh
                    sx={{
                      fontSize: 18,
                      animation: loading ? "spin 1s linear infinite" : "none",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={handleDrawerToggle}
                sx={{
                  p: 0.75,
                  color: "#475569",
                  bgcolor: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  "&:hover": { bgcolor: "#fee2e2", color: "#dc2626" },
                }}
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </HeaderBox>

          {/* ── Filter Tabs & Quick Action Bar ── */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Filter Chips */}
            <Stack direction="row" spacing={0.75}>
              <Chip
                clickable
                size="small"
                onClick={() => setFilterType("all")}
                label={`All (${notifications.length})`}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.74rem",
                  height: 28,
                  borderRadius: 2,
                  bgcolor: filterType === "all" ? "#2563eb" : "#f1f5f9",
                  color: filterType === "all" ? "#ffffff" : "#334155",
                  border: filterType === "all" ? "none" : "1px solid #cbd5e1",
                  "&:hover": {
                    bgcolor: filterType === "all" ? "#1d4ed8" : "#e2e8f0",
                  },
                }}
              />

              <Chip
                clickable
                size="small"
                onClick={() => setFilterType("unread")}
                label={`Unread (${unreadCount})`}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.74rem",
                  height: 28,
                  borderRadius: 2,
                  bgcolor: filterType === "unread" ? "#dc2626" : "rgba(220, 38, 38, 0.08)",
                  color: filterType === "unread" ? "#ffffff" : "#dc2626",
                  border: filterType === "unread" ? "none" : "1px solid rgba(220, 38, 38, 0.3)",
                  "&:hover": {
                    bgcolor: filterType === "unread" ? "#b91c1c" : "rgba(220, 38, 38, 0.16)",
                  },
                }}
              />

              <Chip
                clickable
                size="small"
                onClick={() => setFilterType("pinned")}
                icon={
                  <PushPin
                    sx={{
                      fontSize: "14px !important",
                      color: filterType === "pinned" ? "#ffffff !important" : "#b45309 !important",
                    }}
                  />
                }
                label={`Pinned (${pinnedCount})`}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.74rem",
                  height: 28,
                  borderRadius: 2,
                  bgcolor: filterType === "pinned" ? "#d97706" : "rgba(217, 119, 6, 0.08)",
                  color: filterType === "pinned" ? "#ffffff" : "#b45309",
                  border: filterType === "pinned" ? "none" : "1px solid rgba(217, 119, 6, 0.3)",
                  "&:hover": {
                    bgcolor: filterType === "pinned" ? "#b45309" : "rgba(217, 119, 6, 0.16)",
                  },
                }}
              />
            </Stack>

            {/* Mark All Read */}
            {unreadCount > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={markAllAsRead}
                startIcon={<DoneAll sx={{ fontSize: 16 }} />}
                sx={{
                  fontSize: "0.74rem",
                  fontWeight: 700,
                  textTransform: "none",
                  py: 0.3,
                  px: 1,
                  borderRadius: 1.5,
                  color: "#2563eb",
                  bgcolor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  "&:hover": {
                    bgcolor: "#dbeafe",
                  },
                }}
              >
                Mark all read
              </Button>
            )}
          </Box>

          {/* ── Body Content ── */}
          <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
            {loading && notifications.length === 0 ? (
              <Stack spacing={1.5} sx={{ py: 1 }}>
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={110}
                    sx={{ borderRadius: 3 }}
                  />
                ))}
              </Stack>
            ) : displayedNotifications.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 3,
                  borderRadius: 3,
                  bgcolor: "#ffffff",
                  border: "1px dashed #cbd5e1",
                }}
              >
                <NotificationsNoneOutlined
                  sx={{ fontSize: 50, color: "#94a3b8", mb: 1.5 }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
                  {filterType === "unread"
                    ? "No Unread Notifications"
                    : filterType === "pinned"
                      ? "No Pinned Notifications"
                      : "No Notifications Yet"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5, fontSize: "0.78rem" }}>
                  {filterType === "unread"
                    ? "You are all caught up! New messages will appear here."
                    : filterType === "pinned"
                      ? "Pin important notifications to keep them easily accessible at the top."
                      : "System notices and updates will appear here automatically."}
                </Typography>
              </Box>
            ) : (
              displayedNotifications.map((item) => {
                const id = item.UniqueID || item.id;
                const isUnread = !item.IsRead;
                const isPinned = Boolean(item.IsPinned);
                const meta = getCategoryMeta(item.Type);
                const CategoryIcon = meta.Icon;
                const timeStr = item.CreatedOn ? moment(item.CreatedOn).fromNow() : "Recent";

                return (
                  <NotificationCard
                    key={id}
                    isUnread={isUnread}
                    isPinned={isPinned}
                  >
                    {/* Header Row: Category Icon, Title, and Action Buttons */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2,
                            bgcolor: meta.bg,
                            border: `1.5px solid ${meta.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CategoryIcon sx={{ fontSize: 20, color: meta.color }} />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: isUnread ? 800 : 700,
                              color: "#0f172a",
                              fontSize: "0.88rem",
                              lineHeight: 1.3,
                              wordBreak: "break-word",
                            }}
                          >
                            {item.Title || item.title || "Notification"}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.35 }}>
                            <AccessTimeRounded sx={{ fontSize: 13, color: "#64748b" }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#64748b",
                                fontWeight: 600,
                                fontSize: "0.72rem",
                              }}
                            >
                              {timeStr}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>

                      {/* Quick Top Actions: Pin, Mark Read/Unread, Delete */}
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        {/* Pin / Unpin Button */}
                        <Tooltip title={isPinned ? "Unpin notification" : "Pin to top"} arrow>
                          <IconButton
                            size="small"
                            onClick={() => togglePin(id)}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.75,
                              color: isPinned ? "#b45309" : "#64748b",
                              bgcolor: isPinned ? "#fef3c7" : "#f1f5f9",
                              border: `1px solid ${isPinned ? "#fde68a" : "#e2e8f0"}`,
                              "&:hover": {
                                color: "#b45309",
                                bgcolor: "#fde68a",
                              },
                            }}
                          >
                            {isPinned ? (
                              <PushPin sx={{ fontSize: 16 }} />
                            ) : (
                              <PushPinOutlined sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* Mark Read / Unread Button */}
                        <Tooltip title={isUnread ? "Mark as read" : "Mark as unread"} arrow>
                          <IconButton
                            size="small"
                            onClick={() => (isUnread ? markAsRead(id) : markAsUnread(id))}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.75,
                              color: isUnread ? "#1d4ed8" : "#64748b",
                              bgcolor: isUnread ? "#eff6ff" : "#f1f5f9",
                              border: `1px solid ${isUnread ? "#bfdbfe" : "#e2e8f0"}`,
                              "&:hover": {
                                color: "#1d4ed8",
                                bgcolor: "#dbeafe",
                              },
                            }}
                          >
                            {isUnread ? (
                              <DraftsOutlined sx={{ fontSize: 16 }} />
                            ) : (
                              <MarkEmailReadOutlined sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* Delete / Dismiss */}
                        <Tooltip title="Dismiss notification" arrow>
                          <IconButton
                            size="small"
                            onClick={() => deleteNotification(id)}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.75,
                              color: "#64748b",
                              bgcolor: "#f1f5f9",
                              border: "1px solid #e2e8f0",
                              "&:hover": {
                                color: "#dc2626",
                                bgcolor: "#fee2e2",
                                borderColor: "#fecaca",
                              },
                            }}
                          >
                            <DeleteOutline sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    {/* Message Body */}
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1.25,
                        fontSize: "0.85rem",
                        lineHeight: 1.5,
                        color: "#334155",
                        fontWeight: isUnread ? 600 : 400,
                        whiteSpace: "pre-line",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.Message || item.message || item.subtitle}
                    </Typography>

                    {/* Footer Row: Type Chip, Expiry Tag & Optional Link */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mt: 1.5,
                        pt: 1,
                        borderTop: "1px solid #f1f5f9",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          label={meta.label}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: meta.bg,
                            color: meta.color,
                            border: `1px solid ${meta.border}`,
                            "& .MuiChip-label": { px: 0.8 },
                          }}
                        />

                        {isPinned && (
                          <Chip
                            icon={<PushPin sx={{ fontSize: "12px !important", color: "#b45309 !important" }} />}
                            label="PINNED"
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.64rem",
                              fontWeight: 800,
                              bgcolor: "#fef3c7",
                              color: "#b45309",
                              border: "1px solid #fde68a",
                              "& .MuiChip-label": { px: 0.6 },
                            }}
                          />
                        )}

                        {item.ExpiryDate && (
                          <Chip
                            icon={<ScheduleRounded sx={{ fontSize: "12px !important", color: "#dc2626 !important" }} />}
                            label={`Exp: ${moment(item.ExpiryDate).format("DD MMM, hh:mm A")}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              bgcolor: "#fef2f2",
                              color: "#dc2626",
                              border: "1px solid #fecaca",
                              "& .MuiChip-label": { px: 0.6 },
                            }}
                          />
                        )}
                      </Stack>

                      {item.Link && (
                        <Button
                          component={Link}
                          to={item.Link}
                          onClick={handleDrawerToggle}
                          size="small"
                          endIcon={<OpenInNewOutlined sx={{ fontSize: "13px !important" }} />}
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            textTransform: "none",
                            py: 0.25,
                            px: 1,
                            borderRadius: 1.5,
                            bgcolor: "rgba(37, 99, 235, 0.08)",
                            color: "#1d4ed8",
                            border: "1px solid rgba(37, 99, 235, 0.2)",
                            "&:hover": {
                              bgcolor: "rgba(37, 99, 235, 0.16)",
                              borderColor: "#1d4ed8",
                            },
                          }}
                        >
                          Open Link
                        </Button>
                      )}
                    </Box>
                  </NotificationCard>
                );
              })
            )}
          </Box>

          {/* ── Footer ── */}
          <Divider />
          <Box
            sx={{
              p: 1.5,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: (t) => alpha(t.palette.background.paper, 0.7),
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {user?.Type ? `${user.Type} Notification Panel` : "Notification Center"}
            </Typography>

            {notifications.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={clearNotifications}
                sx={{
                  borderRadius: 2,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  textTransform: "none",
                  py: 0.25,
                  px: 1.2,
                  borderColor: alpha(theme.palette.divider, 0.2),
                }}
              >
                Clear all
              </Button>
            )}
          </Box>
        </Drawer>
      </ThemeProvider>
    </Fragment>
  );
}
